import type { AppLanguage } from '../i18n/types';
import settlementsJson from '../constants/israelSettlements.json';

export type IsraelSettlement = {
  id: number | null;
  he: string;
  en: string;
  nafaId?: number | null;
  nafaName?: string;
};

const SETTLEMENTS: IsraelSettlement[] = settlementsJson as IsraelSettlement[];

const byHebrew = new Map<string, IsraelSettlement>();
const byEnglish = new Map<string, IsraelSettlement>();

for (const settlement of SETTLEMENTS) {
  const he = settlement.he.trim();
  if (!he) {
    continue;
  }
  byHebrew.set(he, settlement);
  const en = settlement.en.trim();
  if (en) {
    byEnglish.set(en.toLowerCase(), settlement);
  }
}

export function israelSettlementCount(): number {
  return SETTLEMENTS.length;
}

export function allIsraelSettlements(): readonly IsraelSettlement[] {
  return SETTLEMENTS;
}

export function isAddressCityField(path: readonly string[], key: string): boolean {
  const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, '_');
  if (normalizedKey !== 'city') {
    return false;
  }
  return path.some((segment) => segment.trim().toLowerCase().replace(/\s+/g, '_') === 'address');
}

export function isAddressStreetField(path: readonly string[], key: string): boolean {
  const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, '_');
  if (normalizedKey !== 'street') {
    return false;
  }
  return path.some((segment) => segment.trim().toLowerCase().replace(/\s+/g, '_') === 'address');
}

export function settlementIdForWireCity(cityWire: string): string | null {
  const settlement = findIsraelSettlementByWire(cityWire);
  if (settlement?.id != null && Number.isFinite(settlement.id)) {
    return String(settlement.id);
  }
  return null;
}

export function findIsraelSettlementByWire(value: string): IsraelSettlement | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return byHebrew.get(trimmed) ?? byEnglish.get(trimmed.toLowerCase()) ?? null;
}

/** UI label for a stored city value (wire is usually Hebrew). */
export function israelSettlementDisplayLabel(value: string, language: AppLanguage): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  const match = findIsraelSettlementByWire(trimmed);
  if (!match) {
    return trimmed;
  }
  if (language === 'he') {
    return match.he;
  }
  return match.en.trim() || match.he;
}

/** Hebrew name stored in address.city (geocoding expects Hebrew localities). */
export function israelSettlementWireValue(settlement: IsraelSettlement): string {
  return settlement.he.trim();
}

export function searchIsraelSettlements(query: string, language: AppLanguage, limit = 80): IsraelSettlement[] {
  const trimmed = query.trim();
  if (!trimmed) {
    return SETTLEMENTS.slice(0, limit);
  }
  const lower = trimmed.toLowerCase();
  const results: IsraelSettlement[] = [];
  for (const settlement of SETTLEMENTS) {
    const he = settlement.he;
    const en = settlement.en.trim();
    const matchesHebrew = he.includes(trimmed) || he.toLowerCase().includes(lower);
    const matchesEnglish = en && en.toLowerCase().includes(lower);
    const matchesDisplay =
      language === 'he'
        ? matchesHebrew
        : matchesEnglish || matchesHebrew;
    if (matchesDisplay) {
      results.push(settlement);
      if (results.length >= limit) {
        break;
      }
    }
  }
  return results;
}

export function settlementOptionLabel(settlement: IsraelSettlement, language: AppLanguage): string {
  if (language === 'he') {
    return settlement.he;
  }
  const en = settlement.en.trim();
  return en ? `${en} — ${settlement.he}` : settlement.he;
}
